import { strings } from '@/services/translation/strings';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { format } from 'date-fns';
import * as React from 'react';
import { useMemo } from 'react';
import { FiCalendar, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

interface DayPickerProps {
  value: Date;
  onChange: (newValue: Date) => void;
  position?: 'left' | 'center';
  showLabels?: boolean;
}

const WEEKDAY_HEADERS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const historyLimit = new Date(Date.UTC(2025, 0, 1));

export const DayPicker: React.FC<DayPickerProps> = ({ value, onChange, position = 'center', showLabels = true }) => {
  const [viewDate, setViewDate] = React.useState<Date>(
    new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), 1))
  );

  const prevDay = () => {
    const newVal = new Date(value);
    newVal.setUTCDate(newVal.getUTCDate() - 1);
    newVal.setUTCHours(0, 0, 0, 0);
    onChange(newVal);
  };

  const nextDay = () => {
    const newVal = new Date(value);
    newVal.setUTCDate(newVal.getUTCDate() + 1);
    newVal.setUTCHours(0, 0, 0, 0);
    onChange(newVal);
  };

  const canGoBack = useMemo(() => {
    const dayBefore = new Date(value);
    dayBefore.setUTCDate(dayBefore.getUTCDate() - 1);
    return dayBefore >= historyLimit;
  }, [value]);

  const canGoBackMonth = useMemo(() => {
    const prevMonth = new Date(Date.UTC(viewDate.getUTCFullYear(), viewDate.getUTCMonth() - 1, 1));
    return prevMonth >= historyLimit;
  }, [viewDate]);

  const goToPrevMonth = () => {
    setViewDate(new Date(Date.UTC(viewDate.getUTCFullYear(), viewDate.getUTCMonth() - 1, 1)));
  };

  const goToNextMonth = () => {
    setViewDate(new Date(Date.UTC(viewDate.getUTCFullYear(), viewDate.getUTCMonth() + 1, 1)));
  };

  React.useEffect(() => {
    setViewDate(new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), 1)));
  }, [value.getUTCFullYear(), value.getUTCMonth()]);

  const calendarDays = useMemo(() => {
    const monthStart = new Date(Date.UTC(viewDate.getUTCFullYear(), viewDate.getUTCMonth(), 1));
    const monthEnd = new Date(Date.UTC(viewDate.getUTCFullYear(), viewDate.getUTCMonth() + 1, 0));

    const gridStart = new Date(monthStart);
    gridStart.setUTCDate(gridStart.getUTCDate() - gridStart.getUTCDay());

    const gridEnd = new Date(monthEnd);
    gridEnd.setUTCDate(gridEnd.getUTCDate() + (6 - gridEnd.getUTCDay()));

    const days: Date[] = [];
    const cursor = new Date(gridStart);
    while (cursor <= gridEnd) {
      days.push(new Date(cursor));
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return days;
  }, [viewDate]);

  const dateDisplay = format(value, 'do MMMM yyyy');
  const viewMonthLabel = format(new Date(viewDate.getUTCFullYear(), viewDate.getUTCMonth(), 1), 'MMMM yyyy');

  const isSameDay = (a: Date, b: Date) =>
    a.getUTCFullYear() === b.getUTCFullYear() &&
    a.getUTCMonth() === b.getUTCMonth() &&
    a.getUTCDate() === b.getUTCDate();

  const isCurrentMonth = (day: Date) =>
    day.getUTCMonth() === viewDate.getUTCMonth() && day.getUTCFullYear() === viewDate.getUTCFullYear();

  return (
    <div className="flex justify-between items-stretch gap-5">
      <button
        type="button"
        aria-label="Previous Day"
        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 px-3 py-2 rounded-lg border bg-white flex items-center gap-1"
        onClick={prevDay}
        disabled={!canGoBack}
      >
        <FiChevronLeft className="w-6 h-6" />
        {showLabels && <p className="hidden sm:block">{strings.common.previous}</p>}
      </button>

      <Popover className="relative w-60">
        <PopoverButton className="h-full w-full py-3 border border-border rounded-md bg-white flex justify-center items-center leading-none px-3 gap-3">
          <FiCalendar />
          {dateDisplay}
        </PopoverButton>

        <PopoverPanel
          className="absolute border border-border bg-white p-5 z-50 shadow w-[320px]"
          style={{ left: position === 'center' ? 'calc(50% - 160px)' : undefined }}
        >
          {({ close }) => (
            <>
              <div className="flex gap-2 justify-center items-center mb-4">
                <button
                  onClick={canGoBackMonth ? goToPrevMonth : undefined}
                  disabled={!canGoBackMonth}
                  aria-label="Previous month"
                >
                  <FiChevronLeft className="w-5 h-5" style={{ color: !canGoBackMonth ? 'grey' : undefined }} />
                </button>
                <div className="py-1 px-6 bg-active rounded-md border border-border font-bold text-sm">
                  {viewMonthLabel}
                </div>
                <button onClick={goToNextMonth} aria-label="Next month">
                  <FiChevronRight className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-7 mb-1">
                {WEEKDAY_HEADERS.map(h => (
                  <div key={h} className="text-center text-xs font-semibold text-gray-400 py-1">
                    {h}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7">
                {calendarDays.map(day => {
                  const isSelected = isSameDay(day, value);
                  const isDisabled = day < historyLimit;
                  const inMonth = isCurrentMonth(day);

                  return (
                    <div key={day.toISOString()} className="flex items-center justify-center p-0.5">
                      <button
                        onClick={
                          isDisabled
                            ? undefined
                            : () => {
                                onChange(day);
                                close();
                              }
                        }
                        disabled={isDisabled}
                        className={[
                          'w-8 h-8 rounded-full text-sm flex items-center justify-center transition-colors',
                          isSelected
                            ? 'bg-blue-600 text-white font-bold'
                            : isDisabled
                              ? 'text-gray-300 cursor-default'
                              : inMonth
                                ? 'hover:bg-gray-100 cursor-pointer'
                                : 'text-gray-400 hover:bg-gray-50 cursor-pointer'
                        ].join(' ')}
                      >
                        {day.getUTCDate()}
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </PopoverPanel>
      </Popover>

      <button
        type="button"
        aria-label="Next Day"
        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 px-3 py-2 rounded-lg border bg-white flex items-center gap-1"
        onClick={nextDay}
      >
        {showLabels && <p className="hidden sm:block">{strings.common.next}</p>}
        <FiChevronRight className="w-6 h-6" />
      </button>
    </div>
  );
};
